import type { CasinoLastWinner } from '@atlas/transport';
import type { CasinoGroup } from 'utils/casino';
import createRequest from 'utils/request';
import createSliceRequest from 'utils/sliceRequest';

export const lastWinnersRequest = createRequest<CasinoLastWinner[]>(
  '/api/gs/lastWinnings:list',
);

export type Game = {
  id: number;
  name: string;
  iconUrl: string;
  favoritesCount: number;
  isDemoAvailable: boolean;
  providerName: string;
};

type GamesListRequest = {
  query: {
    currentSlice: number;
    itemsPerSlice: number;
    group: string;
    name?: string;
    categories?: string[];
    providerNames?: string[];
    sortDirection?: 'asc' | 'desc';
    sortField?: string;
  };
};

type GamesListResponse = {
  games: Game[];
  slice: {
    hasNext: boolean;
  };
};

export const gamesListRequest = createSliceRequest<
  GamesListResponse,
  Game,
  GamesListRequest
>('/api/gs/v2/game', {
  response: (response) => ({
    data: response.games,
    slice: response.slice,
  }),
  request: (props) => ({
    query: {
      ...props.query,
      ...props.slice,
    } as GamesListRequest['query'],
  }),
});

type GameUrlResponse = {
  data: {
    link: string;
    type: 'URL' | 'BASE64';
  };
  game: Game;
};

type GameUrlRequest = {
  query: {
    game: number;
    locale: string;
    providerName?: string;
    onlyUrl?: boolean;
  };
};

export const gameUrlRequest = createRequest<GameUrlResponse, GameUrlRequest>(
  '/api/gs/v2/game/url',
);

export const demoGameUrlRequest = createRequest<
  GameUrlResponse,
  GameUrlRequest
>('/api/gs/v2/game/url-demo');

export type CasinoCategory = {
  id: number;
  name: string;
  position: number;
  iconUrl?: string;
};

type CategoriesResponse = {
  categories: CasinoCategory[];
};

type CategoriesRequest = {
  query: {
    group: CasinoGroup;
  };
};

export const categoriesRequest = createRequest<
  CategoriesResponse,
  CategoriesRequest
>('api/gs/v2/category');

type FavoriteGamesResponse = {
  favoriteGames: number[];
};

type FavoriteGamesRequest = {
  body: {
    gameIds: number[];
  };
};

export const favoriteGamesRequest = createRequest<
  FavoriteGamesResponse,
  FavoriteGamesRequest
>('api/gs/favorites:identify', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
});

type AddFavoriteGameResponse = {
  favoriteGames: number[];
};

type AddFavoriteGameRequest = {
  body: {
    gameId: number;
  };
};

export const addFavoriteGameRequest = createRequest<
  AddFavoriteGameResponse,
  AddFavoriteGameRequest
>('api/gs/favorites:add', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
});

type RemoveFavoriteGameResponse = {
  favoriteGames: number[];
};

type RemoveFavoriteGameRequest = {
  body: {
    gameId: number;
  };
};

export const removeFavoriteGameRequest = createRequest<
  RemoveFavoriteGameResponse,
  RemoveFavoriteGameRequest
>('api/gs/favorites:remove', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
});

type FindFavoriteGamesResponse = {
  games: Game[];
  paging: {
    itemsPerPage: number;
    currentPage: number;
    totalItems: number;
    totalPages: number;
  };
};

type FindFavoriteGamesRequest = {
  query: {
    locale?: string;
  };
};

export const findFavoriteGamesRequest = createSliceRequest<
  FindFavoriteGamesResponse,
  Game,
  FindFavoriteGamesRequest
>(
  '/api/gs/favorites:find',
  {
    request: (props) => ({
      body: {
        paging: {
          currentPage: props.slice.currentSlice,
          itemsPerPage: props.slice.itemsPerSlice,
        },
      },
      query: {
        locale: props.query?.locale,
      },
      ...props,
    }),
    response: ({ games, paging }) => ({
      data: games,
      slice: {
        hasNext: paging.currentPage + 1 < paging.totalPages,
      },
    }),
  },
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  },
);

type CasinoGame = {
  id: number;
  name: string;
  iconUrl: string;
  backgroundUrl?: string;
  favoritesCount: number;
  isDemoAvailable: boolean;
  providerName: string;
  providerDisplayedName: string;
};

type CasinoGameCategory = {
  id: number;
  name: string;
  position: number;
  gameCount: number;
  games: CasinoGame[];
  iconUrl: string;
};

type CategoryGamesResponse = {
  categories: CasinoGameCategory[];
  slice: {
    hasNext: boolean;
    hasPrevious: boolean;
    itemsPerSlice: number;
    sliceNumber: number;
  };
};

type CategoryGamesRequest = {
  query: {
    groupName: CasinoGroup;
    gamesCount: number;
  };
};

export const categoryGamesRequest = createSliceRequest<
  CategoryGamesResponse,
  CasinoGameCategory,
  CategoryGamesRequest
>('/api/gs/v2/category/games', {
  response: (response) => ({
    data: response.categories,
    slice: response.slice,
  }),
  request: (props) => ({
    query: {
      ...props.query,
      ...props.slice,
    } as CategoryGamesRequest['query'],
  }),
});

type Provider = {
  id: number;
  name: string;
  displayedName: string;
  position: number;
  iconUrl: string;
};

type ProvidersResponse = {
  providers: Provider[];
  slice: {
    itemsPerSlice: number;
    sliceNumber: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
};

type ProvidersRequest = {
  query: {
    id?: number;
    category?: string;
    group?: string[];
    currency?: string;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
    searchField?: string;
    search?: string;
    currentSlice: number;
    itemsPerSlice: number;
  };
};

export const providersRequest = createSliceRequest<
  ProvidersResponse,
  Provider,
  ProvidersRequest
>('/api/gs/v2/game/provider', {
  response: (response) => ({
    data: response.providers,
    slice: response.slice,
  }),
  request: (props) => ({
    query: {
      ...props.query,
      ...props.slice,
    } as ProvidersRequest['query'],
  }),
});
